import {
  BRANCHES_TERMINALES,
  ETAPES_DOSSIER,
  type EtapeDescriptor,
  isPaymentMean,
  PAYMENT_MEAN_LABELS,
} from "@hakili/domain-types";
import { formatDate, formatMoney, t } from "@hakili/i18n";
import { GuardedButton, Strip } from "@hakili/ui";
import { ChevronRight, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Settlements, type SettlementLineView } from "./settlements.js";

import type { Vue } from "../shell/views.js";

/**
 * `round CN-A`, `CN-A.1`: **the Dossiers screen, `hakili-console.jsx`'s
 * `CollecteScreen`** , `WF-CON-01` the list and its scope, `WF-CON-02` the
 * supervision band, `WF-CON-03` the new flags.
 *
 * ## What is the door's and what is the screen's
 *
 * The SCOPE is the door's: `GET /api/v1/dossiers` answers a chargé his own
 * files and the administration and the responsable all of them, by
 * `P1-DOS-017`'s one query with the scope as a parameter. This screen never
 * filters by owner; a screen that did would be a second scope rule, and the one
 * that drifts is the one nobody runs in the test that matters.
 *
 * LATENESS is the door's too: `lateBy` is derived in the api beside `2.4.1`'s
 * scale, and the screen must not carry a copy of `DELAI_ETAPE` , it is
 * deliberately not vocabulary. What the screen derives is what it can derive
 * from the rows it was handed: which files are before `soumis`, which are at
 * `pret`, which match a search, and which single file is the latest of all.
 *
 * ## What is a dash, and why
 *
 * The drawing's row prints a last contact, a count of missing pieces and a
 * count of reminders. No door answers any of the three today , the journal is
 * per file, the pieces are per file, and `messaging` sends nothing , and `R81`
 * is exact: a value not yet there is a DASH, never a paragraph and never a
 * number. The « Sans réponse » filter, which needs the reminder count, is
 * greyed with its reason and its round rather than answering an empty list
 * that would read as « nobody is unanswered ».
 */

export interface DossierRow {
  readonly id: string;
  readonly reference: string;
  readonly clientName: string;
  readonly vehicleLabel: string;
  readonly sellerName: string;
  readonly stage: string;
  readonly ownerName: string | null;
  readonly isNew: boolean;
  readonly amountFinanced: number;
  readonly daysInStage: number;
  /** Days beyond the stage's expected duration, or `null`. The API's, never ours. */
  readonly lateBy: number | null;
}

/** One stage of the strip: its name, its count, how many are late, and the list filtered on it. */
const stripStage = (
  id: string,
  name: string,
  band: readonly BandCell[] | null,
): { id: string; name: string; count: number; late: number; href: string } => {
  const cell = band?.find((one) => one.stage === id);
  return {
    id,
    name,
    count: cell?.total ?? 0,
    late: cell?.stagnants ?? 0,
    href: `/dossiers?etape=${encodeURIComponent(id)}`,
  };
};

export interface BandCell {
  readonly stage: string;
  readonly total: number;
  readonly stagnants: number;
}

export type Filter = "active" | "pret" | "tous";

export interface DossiersProps {
  readonly vue: Vue;
  readonly rows: readonly DossierRow[];
  /**
   * Round `WF-CON-11-NEW-BANK-PIPELINE`, `M2` (`OD-42`): the sends proposed to this chargé that wait on her
   * accord , a count, from `GET /proposed-sends`. `undefined` where the root never wired the door (a seat
   * without `soumettreABanque`), `0` when nothing waits; both draw nothing.
   */
  readonly pendingRecommendations?: number | undefined;
  /** The indicator's link: `/recommandations`. */
  readonly onOpenRecommendations?: (() => void) | undefined;
  /** `null` when the view has no band , a chargé , and the screen draws none. */
  readonly band: readonly BandCell[] | null;
  readonly filter: Filter;
  readonly onFilter: (filter: Filter) => void;
  /** The band's own filter, which wins over the named ones. `null` when none. */
  readonly stageFilter: string | null;
  readonly onStageFilter: (stage: string | null) => void;
  readonly search: string;
  readonly onSearch: (text: string) => void;
  readonly onMarkSeen: () => void;
  readonly onOpen: (id: string) => void;
  readonly busy: boolean;
  /**
   * `round CN-C`, `CN-C.6`, WF-CON-45: the encaissements, LIVE from the
   * payments door under the administration and the responsable, `null` while
   * loading, and ABSENT (`undefined`) for a chargé, who never sees a commission
   * figure in any view.
   */
  readonly fileFees?: readonly FileFeeLineView[] | null | undefined;
  /**
   * `T-1c` (WF-CON-45, `P2-PAY-003`): Hakili's confirmation on evidence , the proof filed
   * through the documents' ticket under the fee, then the collect door; the list re-read after.
   */
  readonly collectFileFee?: ((feeId: string, evidence: File) => Promise<void>) | undefined;
  /** `T-1d`: the dealers' declared top-ups, the administration's block beside the encaissements. */
  readonly topups?: readonly TopupLineView[] | null | undefined;
  readonly confirmTopup?: ((topupId: string, evidence: File) => Promise<void>) | undefined;
  /** `T-2` (WF-CON-18, `P2-PAY-025`): the two-leg board under row 26; Hakili's leg observed on evidence (row 24). */
  readonly settlements?: readonly SettlementLineView[] | null | undefined;
  /** `M5`: the settlements read failed. */
  readonly settlementsFailed?: boolean | undefined;
  readonly observeHakiliLeg?:
    ((settlementId: string, evidence: File, transferReference: string | null) => Promise<void>) | undefined;
}

/**
 * The drawing's per-stage chip, `CHIPS_ETAPE`. Keyed by the WIRE's stage id ,
 * `09-01`, a value the api sends is data and never an identifier , and every
 * shade here is one `04` §4.6.3 carries since `CN-A.1`'s amendment.
 */
const STAGE_CHIP = new Map<string, string>([
  ["intake", "bg-gray-100 text-gray-600"],
  ["collecte", "bg-amber-50 text-amber-700"],
  ["expertise", "bg-sky-50 text-sky-700"],
  ["analyse", "bg-indigo-50 text-indigo-700"],
  ["pret", "bg-violet-50 text-violet-700"],
  ["soumis", "bg-emerald-50 text-emerald-700"],
  ["offre", "bg-teal-50 text-teal-700"],
  ["assurance", "bg-cyan-50 text-cyan-700"],
  ["conditions", "bg-blue-50 text-blue-700"],
  ["decaisse", "bg-emerald-100 text-emerald-800"],
  ["refuse", "bg-rose-50 text-rose-600"],
  ["refuseBanque", "bg-rose-50 text-rose-600"],
  ["sansSuite", "bg-gray-100 text-gray-600"],
]);

const STAGES = [...ETAPES_DOSSIER, ...BRANCHES_TERMINALES];
const stageDef = (id: string): EtapeDescriptor | undefined => STAGES.find((stage) => stage.id === id);
const indexOf = (id: string): number => ETAPES_DOSSIER.findIndex((stage) => stage.id === id);

/** Two letters of a name, the drawing's own avatar rule. */
const initialsOf = (nom: string): string =>
  nom
    .split(/\s+/u)
    .filter(Boolean)
    .map((mot) => mot[0] ?? "")
    .join("")
    .slice(0, 2)
    .toLocaleUpperCase("fr");

const DASH = t("common.valeur.tiret");

/** « Déclaré le … · Orange Money · réf. … »: the catalogue's sentence with the door's three facts. */
const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

/** The means' label, the vocabulary's; a code the vocabulary does not know is printed as sent. */
const meanLabelOf = (code: string): string => (isPaymentMean(code) ? PAYMENT_MEAN_LABELS[code] : code);

export function Dossiers(props: DossiersProps): React.JSX.Element {
  const isCaseOwner = props.vue === "collecte";
  const fresh = props.rows.filter((row) => row.isNew);

  /**
   * « En collecte » is BEFORE the bank, read off the lifecycle's order and not
   * off a list of exceptions , the drawing's own comment: it stays true as the
   * lifecycle lengthens at the bottom.
   */
  const soumisAt = indexOf("soumis");
  const active = props.rows.filter((row) => {
    const at = indexOf(row.stage);
    return at >= 0 && at < soumisAt;
  });
  const ready = props.rows.filter((row) => row.stage === "pret");

  const search = props.search.trim().toLocaleLowerCase("fr");
  const visible = useMemo(() => {
    const base =
      props.stageFilter !== null
        ? props.rows.filter((row) => row.stage === props.stageFilter)
        : props.filter === "active"
          ? active
          : props.filter === "pret"
            ? ready
            : props.rows;
    if (search === "") return base;
    return base.filter((row) =>
      [row.clientName, row.reference, row.vehicleLabel, row.sellerName]
        .join(" ")
        .toLocaleLowerCase("fr")
        .includes(search),
    );
  }, [props.rows, props.stageFilter, props.filter, active, ready, search]);

  // The single latest file of the whole scope , derived from the rows, which
  // carry the api's own `lateBy`. The WHY is not here: it needs the pieces and
  // the journal, and a sentence guessing at it would be false at the first
  // change.
  const worst = props.rows
    .filter((row) => row.lateBy !== null)
    .sort((a, b) => (b.lateBy ?? 0) - (a.lateBy ?? 0) || a.reference.localeCompare(b.reference))[0];
  const lateCount = props.rows.filter((row) => row.lateBy !== null).length;

  return (
    <div data-testid="dossiers">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isCaseOwner ? t("console.nav.collecteMien") : t("console.nav.collecte")}
          </h1>
          <p className="mt-1.5 text-sm text-gray-500">
            {isCaseOwner ? t("console.dossiers.sousTitreCharge") : t("console.dossiers.sousTitre")}
          </p>
        </div>
        {/* No « Nouveau dossier » here, and no greyed stand-in (`D-1019-2`): no console door opens a file for a
            client , a dossier is born by the buyer's gesture (`WF-CLI-08`) , and a header action that cannot
            exist is not drawn. */}
      </div>

      {/* `WF-CON-11-NEW-BANK-PIPELINE`, `M2` (`OD-42`): a bank joined the panel and sends wait on her accord.
          Minimal on purpose , a line and a link, only while something waits; the decision is the panel's. */}
      {(props.pendingRecommendations ?? 0) > 0 && props.onOpenRecommendations !== undefined ? (
        <div
          data-testid="dossiers-recommandations"
          className="mb-6 flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-3.5"
        >
          <Sparkles className="h-4 w-4 shrink-0 text-indigo-600" />
          <p className="flex-1 text-sm text-indigo-900">
            {props.pendingRecommendations === 1
              ? t("console.recommandations.enAttenteUne")
              : `${String(props.pendingRecommendations)} ${t("console.recommandations.enAttentePlusieurs")}`}
          </p>
          <button
            type="button"
            data-testid="dossiers-recommandations-ouvrir"
            onClick={props.onOpenRecommendations}
            className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
          >
            {t("console.recommandations.voir")}
          </button>
        </div>
      ) : null}

      {/* `WF-CON-03`: what a chargé sees on arriving , what was handed to him
          since his last visit. He can reassign nothing; he notes that he saw. */}
      {isCaseOwner && fresh.length > 0 ? (
        <div
          data-testid="dossiers-nouveaux"
          className="mb-6 flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-3.5"
        >
          <Sparkles className="h-4 w-4 shrink-0 text-indigo-600" />
          <p className="flex-1 text-sm text-indigo-900">
            {fresh.length === 1
              ? t("console.dossiers.unNouveauConfie")
              : `${String(fresh.length)} ${t("console.dossiers.nouveauxConfies")}`}
          </p>
          <button
            type="button"
            data-testid="dossiers-marquer-vus"
            disabled={props.busy}
            onClick={props.onMarkSeen}
            className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {t("console.dossiers.marquerVus")}
          </button>
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-4 gap-4">
        <Stat
          label={t("console.dossiers.statEnCollecte")}
          value={String(active.length)}
          sub={t("console.dossiers.statEnCollecteSous")}
        />
        <Stat
          label={t("console.dossiers.statRelances")}
          value={DASH}
          sub={t("console.dossiers.statRelancesSous")}
        />
        <Stat
          label={t("console.dossiers.statSansReponse")}
          value={DASH}
          sub={t("console.dossiers.statSansReponseSous")}
        />
        <Stat
          label={t("console.dossiers.statPrets")}
          value={String(ready.length)}
          sub={t("console.dossiers.statPretsSous")}
        />
      </div>

      {/* `WF-CON-02`, the band: responsable and administration, never a chargé
          , the DOOR refuses him and this screen never asks for it on his view. */}
      {props.band === null ? null : (
        <div
          data-testid="dossiers-bande"
          className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white"
        >
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
            <div>
              <h3 className="text-sm font-semibold">{t("console.dossiers.bandeTitre")}</h3>
              <p className="mt-0.5 text-sm text-gray-500">{t("console.dossiers.bandeSous")}</p>
            </div>
            {props.stageFilter === null ? null : (
              <button
                type="button"
                data-testid="dossiers-retirer-filtre"
                onClick={() => {
                  props.onStageFilter(null);
                }}
                className="shrink-0 text-xs font-medium text-indigo-600 hover:text-indigo-700"
              >
                {t("console.dossiers.retirerFiltre")}
              </button>
            )}
          </div>
          <div className="border-b border-gray-100 px-2 py-2">
            <Strip
              label={t("console.dossiers.bandeTitre")}
              testIdPrefix="dossiers-cellule"
              stages={ETAPES_DOSSIER.map((stage) => stripStage(stage.id, stage.court, props.band))}
              exits={BRANCHES_TERMINALES.map((branch) =>
                stripStage(branch.id, branch.court, props.band),
              ).filter((one) => one.count > 0)}
              exitsLabel={t("console.dossiers.sorties")}
              current={props.stageFilter}
              onTimeLabel={t("console.dossiers.aLHeure")}
              lateLabel={(late) => `${String(late)} ${t("console.dossiers.enRetard")}`}
              onSelect={(id) => {
                props.onStageFilter(props.stageFilter === id ? null : id);
              }}
            />
          </div>
          <div className="flex items-start gap-3 px-5 py-3">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
            <p data-testid="dossiers-pire" className="text-sm leading-relaxed text-gray-700">
              {worst === undefined ? (
                t("console.dossiers.aucunRetard")
              ) : (
                <>
                  {t("console.dossiers.pireTraine")} {worst.reference} ({worst.clientName}),{" "}
                  {String(worst.daysInStage)} {t("console.dossiers.pireJours")}{" "}
                  {(stageDef(worst.stage)?.label ?? worst.stage).toLocaleLowerCase("fr")}{" "}
                  {t("console.dossiers.pireContre")} {String(worst.daysInStage - (worst.lateBy ?? 0))}{" "}
                  {t("console.dossiers.pireAttendus")} {String(worst.lateBy ?? 0)}{" "}
                  {t("console.dossiers.pireDeTrop")}{" "}
                  {worst.ownerName === null
                    ? t("console.dossiers.sansCharge")
                    : `${t("console.dossiers.portePar")} ${worst.ownerName}.`}
                  {lateCount > 1 ? ` ${String(lateCount - 1)} ${t("console.dossiers.autresDepassent")}` : ""}
                </>
              )}
            </p>
          </div>
        </div>
      )}

      <div className="mb-3 flex items-center gap-2">
        <FilterButton
          isActive={props.filter === "active" && props.stageFilter === null}
          onClick={() => {
            props.onFilter("active");
          }}
        >
          {t("console.dossiers.filtreEnCours")}
        </FilterButton>
        {/* « Sans réponse » waits on the reminder count, which waits on `messaging`: the filter is not drawn until
            it can be true (`D-1019-2`) , an absent filter cannot read as « nobody is unanswered ». */}
        <FilterButton
          isActive={props.filter === "pret" && props.stageFilter === null}
          onClick={() => {
            props.onFilter("pret");
          }}
        >
          {t("console.dossiers.filtrePrets")}
        </FilterButton>
        <FilterButton
          isActive={props.filter === "tous" && props.stageFilter === null}
          onClick={() => {
            props.onFilter("tous");
          }}
        >
          {t("console.dossiers.filtreTous")}
        </FilterButton>
        <input
          type="search"
          data-testid="dossiers-recherche"
          value={props.search}
          onChange={(event) => {
            props.onSearch(event.target.value);
          }}
          placeholder={t("console.dossiers.rechercher")}
          className="ml-auto w-72 rounded-lg border border-gray-200 px-3 py-2 text-xs outline-none focus:border-indigo-200"
        />
      </div>

      <div className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="flex items-center gap-4 border-b border-gray-100 px-5 py-2.5">
          <span className="w-48 text-xs text-gray-500">{t("console.dossiers.colClient")}</span>
          <span className="flex-1 text-xs text-gray-500">{t("console.dossiers.colVehicule")}</span>
          <span className="w-28 text-right text-xs text-gray-500">{t("console.dossiers.colMontant")}</span>
          <span className="w-40 text-xs text-gray-500">{t("console.dossiers.colEtape")}</span>
          <span className="w-32 text-right text-xs text-gray-500">{t("console.dossiers.colContact")}</span>
          {isCaseOwner ? null : (
            <span className="w-10 text-center text-xs text-gray-500">{t("console.dossiers.colCharge")}</span>
          )}
        </div>
        {visible.length === 0 ? (
          <p data-testid="dossiers-vide" className="px-5 py-8 text-center text-sm text-gray-500">
            {isCaseOwner && props.rows.length === 0
              ? t("console.dossiers.listeVideCharge")
              : t("console.dossiers.listeVide")}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {visible.map((row) => (
              <li key={row.id}>
                {/*
                 * `DX-007` (D-1018-6): **a file in the list is a LINK.** An anchor with the
                 * file's own address, so the keyboard reaches it (Tab, Enter), the browser
                 * offers « ouvrir dans un nouvel onglet », and a modifier-click does it. A
                 * plain click still opens the file in place, without a page load.
                 */}
                <a
                  href={`/dossiers/${row.id}`}
                  data-testid={`dossier-${row.reference}`}
                  onClick={(event) => {
                    if (event.defaultPrevented || event.button !== 0) return;
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                    event.preventDefault();
                    props.onOpen(row.id);
                  }}
                  className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600"
                >
                  <div className="w-48 shrink-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{row.clientName}</p>
                      {row.isNew ? (
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                          {t("console.dossiers.badgeNouveau")}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-gray-500">{row.reference}</p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-gray-700">{row.vehicleLabel}</p>
                    <p className="truncate text-xs text-gray-500">{row.sellerName}</p>
                  </div>
                  <span className="w-28 shrink-0 text-right text-sm font-medium tabular-nums">
                    {formatMoney(row.amountFinanced)}
                  </span>
                  <span className="w-40 shrink-0">
                    <span
                      className={
                        "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
                        (STAGE_CHIP.get(row.stage) ?? "")
                      }
                    >
                      {stageDef(row.stage)?.court ?? row.stage}
                    </span>
                  </span>
                  {/* The journal is per file and no door answers it on the list , a dash. */}
                  <span className="w-32 shrink-0 text-right text-xs text-gray-500">{DASH}</span>
                  {isCaseOwner ? null : (
                    <span
                      title={
                        row.ownerName === null
                          ? t("console.dossiers.aucunChargeAffecte")
                          : `${t("console.dossiers.chargeDossier")} ${row.ownerName}`
                      }
                      className="flex w-10 shrink-0 justify-center"
                    >
                      <span
                        className={
                          "flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold " +
                          (row.ownerName === null
                            ? "bg-rose-100 text-rose-700"
                            : "bg-indigo-100 text-indigo-700")
                        }
                      >
                        {row.ownerName === null ? "!" : initialsOf(row.ownerName)}
                      </span>
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* `CN-C.6`: the encaissements LIVE from payments' door (the 2 % on the
          FINANCED amount by the door, never recomputed here), under the
          administration and the responsable alone; the placements in words
          with the insurance round named. A chargé sees neither (WF-CON-44). */}
      {props.vue === "resp" ? (
        // `M1(b)`: greyed with its reason, in the drawing's manner , the block
        // is drawn and its figures are not the responsable's to read.
        <div
          data-testid="dossiers-encaissements-reserve"
          title={t("console.dossiers.encaissementsAdministrationSeule")}
          className="mb-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-5 py-3"
        >
          <h2 className="text-sm font-semibold">{t("console.dossiers.encaissementsTitre")}</h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("console.dossiers.encaissementsAdministrationSeule")}
          </p>
        </div>
      ) : null}
      {props.fileFees === undefined ? null : (
        <FileFees lines={props.fileFees} collect={props.collectFileFee} />
      )}
      {props.topups === undefined ? null : <Topups lines={props.topups} confirm={props.confirmTopup} />}
      {props.settlements === undefined ? null : (
        <Settlements
          lines={props.settlements}
          failed={props.settlementsFailed === true}
          observe={props.observeHakiliLeg}
        />
      )}
      {props.fileFees === undefined ? null : (
        <div
          data-testid="dossiers-placements"
          className="mb-4 rounded-2xl border border-gray-200 bg-white px-5 py-3"
        >
          <h2 className="text-sm font-semibold">{t("console.dossiers.placementsTitre")}</h2>
          <div className="mt-2 grid grid-cols-4 gap-3">
            {(
              [
                "console.dossiers.placementsPolices",
                "console.dossiers.placementsSignature",
                "console.dossiers.placementsRenouvellements",
                "console.dossiers.placementsCommissions",
              ] as const
            ).map((key) => (
              <div key={key}>
                <p className="text-xs text-gray-500">{t(key)}</p>
                <p className="mt-0.5 text-xl font-semibold tabular-nums text-gray-500">
                  {t("common.valeur.tiret")}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-500">{t("console.dossiers.placementsRond")}</p>
        </div>
      )}
      <InWords
        titre={t("console.dossiers.suiviBancaireTitre")}
        mots={t("console.dossiers.suiviBancaireMots")}
        testid="dossiers-suivi-bancaire"
      />
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
}): React.JSX.Element {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      <p className="mt-1 text-xs text-gray-500">{sub}</p>
    </div>
  );
}

function FilterButton({
  isActive,
  onClick,
  children,
}: {
  readonly isActive: boolean;
  readonly onClick: () => void;
  readonly children: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-lg border px-3 py-2 text-xs font-medium transition-colors " +
        (isActive
          ? "border-indigo-200 bg-indigo-50 text-indigo-700"
          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50")
      }
    >
      {children}
    </button>
  );
}

/** `CN-C.6`, WF-CON-45: one line per dossier with an accepted mandate, as the door answers it. */
export interface FileFeeLineView {
  readonly id?: string;
  readonly dossierId: string;
  readonly reference: string;
  readonly clientName: string;
  readonly bankName: string | null;
  readonly amountFinanced: number;
  readonly stage: string;
  readonly mandateAcceptedAt: string | null;
  readonly feeAmount: number;
  readonly feePct: number;
  /** `T-1c`: `declare` (the buyer's word) and `finance` (the bank's) beside the two of `CN-C.6`. */
  readonly status: "attente" | "declare" | "encaisse" | "finance";
  readonly declaration?: { readonly means: string; readonly reference: string; readonly at: string } | null;
  readonly collectedAt?: string | null;
  /** `T-2b` (`P2-PAY-037`): the refund ordered when the file ended without a disbursement, confirmed on evidence. */
  readonly refund?: {
    readonly reason: string;
    readonly orderedAt: string;
    readonly confirmedAt: string | null;
  } | null;
}

function FileFees({
  lines,
  collect,
}: {
  readonly lines: readonly FileFeeLineView[] | null;
  readonly collect?: ((feeId: string, evidence: File) => Promise<void>) | undefined;
}): React.JSX.Element {
  // `T-1c`: the one line being confirmed, its proof chosen, the door's refusal in words.
  const [confirming, setConfirming] = useState<{ id: string; word: string | null; busy: boolean } | null>(
    null,
  );
  return (
    <div
      data-testid="dossiers-encaissements"
      className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white"
    >
      <div className="px-5 py-3">
        <h2 className="text-sm font-semibold">{t("console.dossiers.encaissementsTitre")}</h2>
        <p className="mt-0.5 text-sm text-gray-500">{t("console.dossiers.encaissementsSous")}</p>
      </div>
      {lines === null ? (
        <p className="px-5 pb-4 text-sm text-gray-500">{t("common.state.chargement")}</p>
      ) : lines.length === 0 ? (
        <p data-testid="encaissements-vide" className="px-5 pb-4 text-sm text-gray-500">
          {t("console.dossiers.encaissementsVide")}
        </p>
      ) : (
        <>
          <div className="flex items-center gap-4 border-y border-gray-100 bg-gray-50 px-5 py-2">
            <span className="w-44 text-xs text-gray-500">{t("console.dossiers.encColClient")}</span>
            <span className="w-36 text-xs text-gray-500">{t("console.dossiers.colBanque")}</span>
            <span className="w-32 text-right text-xs text-gray-500">{t("console.dossiers.colFinance")}</span>
            <span className="w-28 text-xs text-gray-500">{t("console.dossiers.encColEtape")}</span>
            <span className="w-32 text-xs text-gray-500">{t("console.dossiers.colMandat")}</span>
            <span className="w-32 text-right text-xs text-gray-500">{t("console.dossiers.colFrais")}</span>
            <span className="flex-1 text-right text-xs text-gray-500">{t("console.dossiers.colStatut")}</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {lines.map((line) => (
              <li
                key={line.dossierId}
                data-testid={`encaissement-${line.reference}`}
                data-row-state={line.status === "encaisse" || line.status === "finance" ? "done" : "pending"}
                className="flex flex-wrap items-center gap-4 px-5 py-3"
              >
                <div className="w-44 shrink-0">
                  <p className="text-sm font-medium text-gray-900">{line.clientName}</p>
                  <p className="text-xs text-gray-500">{line.reference}</p>
                </div>
                <span className="w-36 shrink-0 text-sm text-gray-700">
                  {line.bankName ?? t("common.valeur.tiret")}
                </span>
                <span className="w-32 shrink-0 text-right text-sm tabular-nums text-gray-700">
                  {formatMoney(line.amountFinanced)}
                </span>
                <span className="w-28 shrink-0 text-xs text-gray-500">{stageShortOf(line.stage)}</span>
                <span className="w-32 shrink-0 text-xs text-gray-500">
                  {line.mandateAcceptedAt === null
                    ? t("common.valeur.tiret")
                    : formatDate(new Date(line.mandateAcceptedAt))}
                </span>
                <span
                  data-testid="encaissement-frais"
                  className="w-32 shrink-0 text-right text-sm font-medium tabular-nums"
                >
                  {formatMoney(line.feeAmount)}
                </span>
                <span className="flex flex-1 flex-col items-end gap-1">
                  <span
                    data-testid="encaissement-statut"
                    className={
                      "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
                      (line.status === "encaisse" && (line.refund ?? null) === null
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700")
                    }
                  >
                    {/* `T-2b` (`P2-PAY-037`): the refund ordered on a file that ended without a disbursement, then confirmed on evidence. */}
                    {line.refund !== null && line.refund !== undefined
                      ? line.refund.confirmedAt === null
                        ? t("console.dossiers.statutRemboursementOrdonne")
                        : fill("console.dossiers.statutRembourse", {
                            date: formatDate(new Date(line.refund.confirmedAt)),
                          })
                      : line.status === "encaisse"
                        ? t("console.dossiers.statutEncaisse")
                        : line.status === "declare"
                          ? t("console.dossiers.statutDeclare")
                          : line.status === "finance"
                            ? t("console.dossiers.statutFinance")
                            : t("console.dossiers.statutAttente")}
                  </span>
                  {/* `T-1c`: the confirmation is a door , on evidence, never a free-text receipt. */}
                  {line.status === "finance" ? (
                    <span data-testid="encaissement-regle" className="text-right text-xs text-gray-500">
                      {t("console.dossiers.confirmerEncaissementMotifFinance")}
                    </span>
                  ) : line.status === "encaisse" ? (
                    <span data-testid="encaissement-regle" className="text-right text-xs text-gray-500">
                      {fill("console.dossiers.confirmerEncaissementMotifEncaisse", {
                        date:
                          line.collectedAt === null || line.collectedAt === undefined
                            ? t("common.valeur.tiret")
                            : formatDate(new Date(line.collectedAt)),
                      })}
                    </span>
                  ) : collect === undefined || line.id === undefined ? (
                    <GuardedButton
                      disabled
                      reason={t("console.dossiers.marquerEncaisseMotif")}
                      reasonTone="neutral"
                      variant="secondary"
                      testId="encaissement-marquer"
                    >
                      {t("console.dossiers.confirmerEncaissement")}
                    </GuardedButton>
                  ) : (
                    <button
                      type="button"
                      data-testid="encaissement-marquer"
                      onClick={() => {
                        setConfirming({ id: line.id ?? "", word: null, busy: false });
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t("console.dossiers.confirmerEncaissement")}
                    </button>
                  )}
                </span>
                {line.declaration === null || line.declaration === undefined ? null : (
                  <p data-testid="encaissement-declaration" className="basis-full text-xs text-gray-500">
                    {fill("console.dossiers.declarationLigne", {
                      date: formatDate(new Date(line.declaration.at)),
                      means: meanLabelOf(line.declaration.means),
                      reference: line.declaration.reference,
                    })}
                  </p>
                )}
                {confirming !== null && confirming.id === line.id && collect !== undefined ? (
                  <form
                    data-testid="encaissement-preuve"
                    className="basis-full rounded-xl bg-gray-50 px-4 py-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const input = event.currentTarget.elements.namedItem("evidence");
                      const file = input instanceof HTMLInputElement ? (input.files?.[0] ?? null) : null;
                      if (file === null) {
                        setConfirming({ ...confirming, word: t("console.dossiers.preuveRequise") });
                        return;
                      }
                      setConfirming({ ...confirming, busy: true, word: null });
                      void collect(confirming.id, file)
                        .then(() => {
                          setConfirming(null);
                        })
                        .catch((failure: unknown) => {
                          setConfirming({
                            ...confirming,
                            busy: false,
                            word:
                              (failure as { message?: string } | null)?.message ??
                              t("common.error.connexionEchouee"),
                          });
                        });
                    }}
                  >
                    <label className="text-xs text-gray-600">
                      {t("console.dossiers.preuveTitre")}
                      <input
                        name="evidence"
                        type="file"
                        accept="image/*,application/pdf"
                        data-testid="encaissement-preuve-fichier"
                        className="mt-1 block text-xs"
                      />
                    </label>
                    {confirming.word === null ? null : (
                      <p role="alert" data-testid="encaissement-refus" className="mt-1 text-xs text-rose-600">
                        {confirming.word}
                      </p>
                    )}
                    <div className="mt-2 flex gap-2">
                      <button
                        type="submit"
                        disabled={confirming.busy}
                        data-testid="encaissement-confirmer"
                        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                      >
                        {t("console.dossiers.confirmerEncaissement")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirming(null);
                        }}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
                      >
                        {t("common.action.annuler")}
                      </button>
                    </div>
                  </form>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** `T-1d`: one declared top-up, as `GET /api/v1/payments/topups` answers it. */
export interface TopupLineView {
  readonly id: string;
  readonly organisationId: string;
  readonly organisationName: string;
  readonly amount: number;
  readonly transferReference: string;
  readonly declaredAt: string;
  readonly declaredBy: string;
  readonly confirmedAt: string | null;
  readonly evidenceDocumentId: string | null;
  readonly movementReference: string | null;
}

/**
 * `T-1d` (WF-DLR-13 step 3 as amended by record, `P2-PAY-008`): **the dealers'
 * top-ups, declared by their own door and confirmed here on evidence.** A block
 * of its own beside the encaissements: WF-CON-45's acceptance keeps every
 * dealer off the file-fees block, and a top-up is not a fee , it is money the
 * dealer sent to Hakili's account, which the ledger credits when Hakili's hand
 * confirms it on the proof of receipt. The same form as the fee's, the same
 * refusal in words when no proof is chosen; the door's own sentence otherwise.
 */
function Topups({
  lines,
  confirm,
}: {
  readonly lines: readonly TopupLineView[] | null;
  readonly confirm?: ((topupId: string, evidence: File) => Promise<void>) | undefined;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState<{ id: string; word: string | null; busy: boolean } | null>(
    null,
  );
  return (
    <div
      data-testid="dossiers-recharges"
      className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white"
    >
      <div className="px-5 py-3">
        <h2 className="text-sm font-semibold">{t("console.dossiers.rechargesTitre")}</h2>
        <p className="mt-0.5 text-sm text-gray-500">{t("console.dossiers.rechargesSous")}</p>
      </div>
      {lines === null ? (
        <p className="px-5 pb-4 text-sm text-gray-500">{t("common.state.chargement")}</p>
      ) : lines.length === 0 ? (
        <p data-testid="recharges-vide" className="px-5 pb-4 text-sm text-gray-500">
          {t("console.dossiers.rechargesVide")}
        </p>
      ) : (
        <>
          <div className="flex items-center gap-4 border-y border-gray-100 bg-gray-50 px-5 py-2">
            <span className="w-56 text-xs text-gray-500">{t("console.dossiers.rechargeColConcession")}</span>
            <span className="flex-1 text-xs text-gray-500">{t("console.dossiers.rechargeColVirement")}</span>
            <span className="w-32 text-right text-xs text-gray-500">
              {t("console.dossiers.rechargeColMontant")}
            </span>
            <span className="w-64 text-right text-xs text-gray-500">{t("console.dossiers.colStatut")}</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {lines.map((line) => (
              <li
                key={line.id}
                data-testid={`recharge-${line.id}`}
                data-row-state={line.confirmedAt === null ? "pending" : "done"}
                className="flex flex-wrap items-center gap-4 px-5 py-3"
              >
                <span className="w-56 shrink-0 text-sm font-medium text-gray-900">
                  {line.organisationName}
                </span>
                <span className="flex-1 text-sm text-gray-500">
                  {fill("console.dossiers.rechargeDeclareeLigne", {
                    date: formatDate(new Date(line.declaredAt)),
                    name: line.declaredBy,
                    reference: line.transferReference,
                  })}
                </span>
                <span
                  data-testid="recharge-montant"
                  className="w-32 shrink-0 text-right text-sm font-medium tabular-nums"
                >
                  {formatMoney(line.amount)}
                </span>
                <span className="flex w-64 shrink-0 flex-col items-end gap-1">
                  <span
                    data-testid="recharge-statut"
                    className={
                      "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
                      (line.confirmedAt === null
                        ? "bg-amber-50 text-amber-700"
                        : "bg-emerald-50 text-emerald-700")
                    }
                  >
                    {line.confirmedAt === null
                      ? t("console.dossiers.rechargeStatutDeclaree")
                      : fill("console.dossiers.rechargeStatutConfirmee", {
                          reference: line.movementReference ?? t("common.valeur.tiret"),
                        })}
                  </span>
                  {line.confirmedAt !== null ? (
                    <span data-testid="recharge-confirmee" className="text-right text-xs text-gray-500">
                      {fill("console.dossiers.rechargeConfirmeeMotif", {
                        date: formatDate(new Date(line.confirmedAt)),
                        reference: line.movementReference ?? t("common.valeur.tiret"),
                      })}
                    </span>
                  ) : confirm === undefined ? (
                    <GuardedButton
                      disabled
                      reason={t("console.dossiers.encaissementsAdministrationSeule")}
                      variant="secondary"
                      testId="recharge-confirmer"
                    >
                      {t("console.dossiers.confirmerEncaissement")}
                    </GuardedButton>
                  ) : (
                    <button
                      type="button"
                      data-testid="recharge-confirmer"
                      onClick={() => {
                        setConfirming({ id: line.id, word: null, busy: false });
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t("console.dossiers.confirmerEncaissement")}
                    </button>
                  )}
                </span>
                {confirming !== null && confirming.id === line.id && confirm !== undefined ? (
                  <form
                    data-testid="recharge-preuve"
                    className="basis-full rounded-xl bg-gray-50 px-4 py-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const input = event.currentTarget.elements.namedItem("evidence");
                      const file = input instanceof HTMLInputElement ? (input.files?.[0] ?? null) : null;
                      if (file === null) {
                        setConfirming({ ...confirming, word: t("console.dossiers.rechargePreuveRequise") });
                        return;
                      }
                      setConfirming({ ...confirming, busy: true, word: null });
                      void confirm(confirming.id, file)
                        .then(() => {
                          setConfirming(null);
                        })
                        .catch((failure: unknown) => {
                          setConfirming({
                            ...confirming,
                            busy: false,
                            word:
                              (failure as { message?: string } | null)?.message ??
                              t("common.error.connexionEchouee"),
                          });
                        });
                    }}
                  >
                    <label className="text-xs text-gray-600">
                      {t("console.dossiers.rechargePreuveTitre")}
                      <input
                        name="evidence"
                        type="file"
                        accept="image/*,application/pdf"
                        data-testid="recharge-preuve-fichier"
                        className="mt-1 block text-xs"
                      />
                    </label>
                    {confirming.word === null ? null : (
                      <p role="alert" data-testid="recharge-refus" className="mt-1 text-xs text-rose-600">
                        {confirming.word}
                      </p>
                    )}
                    <div className="mt-2 flex gap-2">
                      <button
                        type="submit"
                        disabled={confirming.busy}
                        data-testid="recharge-valider"
                        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                      >
                        {t("console.dossiers.confirmerEncaissement")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirming(null);
                        }}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
                      >
                        {t("common.action.annuler")}
                      </button>
                    </div>
                  </form>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

const stageShortOf = (stage: string): string =>
  ETAPES_DOSSIER.find((one) => one.id === stage)?.court ?? stage;

function InWords({
  titre,
  sous,
  mots,
  testid,
}: {
  readonly titre: string;
  readonly sous?: string;
  readonly mots: string;
  readonly testid: string;
}): React.JSX.Element {
  return (
    <div data-testid={testid} className="mb-4 rounded-2xl border border-gray-200 bg-white px-5 py-3">
      <h3 className="text-sm font-semibold">{titre}</h3>
      {sous === undefined ? null : <p className="mt-0.5 text-sm text-gray-500">{sous}</p>}
      <p className="mt-2 text-sm text-gray-500">{mots}</p>
    </div>
  );
}

// ---------------------------------------------------------------- the wrapper

export interface DossiersScreenProps {
  readonly vue: Vue;
  /**
   * `WF-CON-11-NEW-BANK-PIPELINE`, `M2`: how many proposed sends wait on this seat, asked once with the list.
   * Wired by the root only where the seat holds `soumettreABanque`; a refusal draws nothing.
   */
  readonly readPendingRecommendations?: (() => Promise<number>) | undefined;
  readonly onOpenRecommendations?: (() => void) | undefined;
  readonly listDossiers: () => Promise<readonly DossierRow[]>;
  readonly readSupervision: () => Promise<readonly BandCell[]>;
  readonly markDossiersSeen: () => Promise<readonly string[]>;
  /** `CN-C.6`: payments' file-fees door; called under the administration and the responsable alone. */
  readonly readFileFees: () => Promise<readonly FileFeeLineView[]>;
  /** `T-1c`: Hakili's confirmation on evidence, then the list re-read. */
  readonly collectFileFee?: (feeId: string, evidence: File) => Promise<void>;
  /** `T-1d`: the dealers' declared top-ups and Hakili's confirmation on evidence, then re-read. */
  readonly readTopups?: () => Promise<readonly TopupLineView[]>;
  readonly confirmTopup?: (topupId: string, evidence: File) => Promise<void>;
  /** `T-2`: the board's read (row 26) and the observation (row 24, the administration alone). */
  readonly readSettlements?: (() => Promise<readonly SettlementLineView[]>) | undefined;
  readonly observeHakiliLeg?: (
    settlementId: string,
    evidence: File,
    transferReference: string | null,
  ) => Promise<void>;
  readonly onOpen: (id: string) => void;
  /** The band's filter lives in the URL so it survives a round trip into a file. */
  readonly stageFilter: string | null;
  readonly onStageFilter: (stage: string | null) => void;
}

/**
 * The layer that asks. It asks ONCE on the view, and again only after a
 * gesture that changed the rows , round 185's lesson about an effect that
 * depended on the object holding its callbacks.
 */
const isLive = (run: { readonly live: boolean }): boolean => run.live;

export function DossiersScreen(props: DossiersScreenProps): React.JSX.Element | null {
  const [rows, setRows] = useState<readonly DossierRow[] | null>(null);
  const [band, setBand] = useState<readonly BandCell[] | null>(null);
  const [fileFees, setFileFees] = useState<readonly FileFeeLineView[] | null>(null);
  const [topups, setTopups] = useState<readonly TopupLineView[] | null>(null);
  const [settlements, setSettlements] = useState<readonly SettlementLineView[] | null>(null);
  const [settlementsFailed, setSettlementsFailed] = useState(false);
  const [filter, setFiltre] = useState<Filter>("active");
  const [search, setRecherche] = useState("");
  const [busy, setBusy] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const [pending, setPending] = useState<number>(0);

  const { vue, listDossiers, readSupervision, readFileFees, readTopups, readSettlements } = props;
  const { readPendingRecommendations } = props;
  useEffect(() => {
    const run: { live: boolean } = { live: true };
    void (async () => {
      const list = await listDossiers();
      if (!run.live) return;
      setRows(list);
      // `WF-CON-11-NEW-BANK-PIPELINE`, `M2`: the recommendations waiting on this seat , a count, and a
      // refusal or a failure is no reason to hide the list: the indicator simply does not draw.
      if (readPendingRecommendations !== undefined) {
        const waiting = await readPendingRecommendations().catch(() => 0);
        if (isLive(run)) setPending(waiting);
      }
      // The band is the responsable's and the administration's. The door
      // refuses a chargé, and asking it on his view would be a 403 he never
      // needed to see.
      if (vue !== "collecte") {
        // `S2-15` residual, `S2-FINDINGS-2` `M3b`: a refusal here must not end the block before the
        // settlements below are asked, same shape as the fee door's own `.catch` further down.
        const cells = await readSupervision().catch(() => []);
        // Asked again after the SECOND await: the narrowing from the first
        // check is stale by now, and a state set on an unmounted screen is
        // the leak React warns about.
        if (isLive(run)) setBand(cells);
      }
      // `CN-C.6`, and `round CN-H`, `M1(b)`: the encaissements are the
      // ADMINISTRATION's alone , `06` §6.1.5 row 24 `accederDonneesFinancieres`
      // (admin « oui », every other chair « non »), and WF-CON-45's actor line
      // amended to it by the founder's word of 13 September. The responsable's
      // screen never asks the door it would be refused on; the block is greyed
      // with that reason in its place. A chargé's view never asks either.
      if (vue === "admin") {
        const fees = await readFileFees().catch(() => []);
        if (isLive(run)) setFileFees(fees);
        // `T-1d`: the top-ups under the same right (row 24), asked on the same view.
        if (readTopups !== undefined) {
          const declared = await readTopups().catch(() => []);
          if (isLive(run)) setTopups(declared);
        }
      }
      /**
       * `S2-15`, `PB-2L`, `M3` (`P1-CNW-057`) , **asked on every view that DRAWS it.**
       *
       * `T-2`'s own comment said it and the code did not do it: *« the two-leg board, every view that
       * holds row 26 »*. The call sat inside the `admin` branch above while the panel is drawn wherever
       * `readSettlements` is wired, so on the responsable's and the chargé's views the door was never
       * called, `settlements` stayed `null`, and « Règlements de vente » printed « Chargement… » for as
       * long as the screen was open. It was not slow and it was not failing: **nobody ever asked.**
       *
       * `06` §6.1.5 row 26, quoted, decides which cure this is:
       *
       *     | 26 | `voirReglementsVentes` | Voir l'état des règlements de vente et de leurs volets
       *       | oui | oui | oui | non | non |
       *
       * , `admin` **oui**, `resp` **oui**, `charge` **oui**, `relB` non, `relC` non. Three chairs hold
       * it, so the panel belongs on their screens and what was missing was the ASKING. The condition is
       * the wiring itself: the composition root passes `readSettlements` to the views that hold the
       * right and to no others (`holdsSettlementsRight`, since `S2-FINDINGS-2` `M5` , it passed it to every
       * view before), which is the same rule read once instead of twice.
       */
      if (readSettlements !== undefined) {
        // `M5`: a failed read is its own state, so a refusal never reads as « nothing to show ».
        const board = await readSettlements().catch(() => null);
        if (isLive(run)) {
          setSettlementsFailed(board === null);
          setSettlements(board ?? []);
        }
      }
    })();
    return () => {
      run.live = false;
    };
  }, [
    vue,
    listDossiers,
    readSupervision,
    readFileFees,
    readTopups,
    readSettlements,
    readPendingRecommendations,
    epoch,
  ]);

  if (rows === null) return null;
  return (
    <Dossiers
      vue={vue}
      rows={rows}
      pendingRecommendations={pending}
      onOpenRecommendations={props.onOpenRecommendations}
      band={vue === "collecte" ? null : band}
      fileFees={vue === "admin" ? fileFees : undefined}
      topups={vue === "admin" && readTopups !== undefined ? topups : undefined}
      settlements={readSettlements === undefined ? undefined : settlements}
      settlementsFailed={settlementsFailed}
      observeHakiliLeg={
        props.observeHakiliLeg === undefined
          ? undefined
          : async (settlementId, evidence, transferReference) => {
              await props.observeHakiliLeg?.(settlementId, evidence, transferReference);
              setEpoch((now) => now + 1);
            }
      }
      confirmTopup={
        props.confirmTopup === undefined
          ? undefined
          : async (topupId, evidence) => {
              await props.confirmTopup?.(topupId, evidence);
              setEpoch((now) => now + 1);
            }
      }
      collectFileFee={
        props.collectFileFee === undefined
          ? undefined
          : async (feeId, evidence) => {
              await props.collectFileFee?.(feeId, evidence);
              setEpoch((now) => now + 1);
            }
      }
      filter={filter}
      onFilter={(next) => {
        setFiltre(next);
        props.onStageFilter(null);
      }}
      stageFilter={props.stageFilter}
      onStageFilter={props.onStageFilter}
      search={search}
      onSearch={setRecherche}
      busy={busy}
      onOpen={props.onOpen}
      onMarkSeen={() => {
        void (async () => {
          setBusy(true);
          try {
            await props.markDossiersSeen();
            setEpoch((now) => now + 1);
          } finally {
            setBusy(false);
          }
        })();
      }}
    />
  );
}
